import { useEffect, useRef, useState } from "react";
import { getVoiceClip } from "@/lib/voiceClips";
import { meteredAudio } from "@/lib/voiceMeter";
import { aiError } from "./aiError";

// Editor playback: when the playing source reaches a comment, pause, speak it in the chosen voice, then resume.
export function useEditorPlayback({ playerRef, comments, voice, enabled, setSpeakingId }) {
  const [playing, setPlaying] = useState(false);
  const commentsRef = useRef(comments);
  const voiceRef = useRef(voice);
  const enabledRef = useRef(enabled);
  commentsRef.current = comments;
  voiceRef.current = voice;
  enabledRef.current = enabled;

  useEffect(() => {
    if (!enabled) return;
    let last = null, busy = false, audio = null, stopped = false;
    const done = new Set();
    const speak = (url) => new Promise((res) => {
      audio = meteredAudio(url);
      audio.onended = res;
      audio.onerror = res;
      audio.play().catch(res);
    });
    const iv = setInterval(async () => {
      const p = playerRef.current;
      if (busy || !p?.getPlayerState) return;
      let state, t;
      try { state = p.getPlayerState(); t = p.getCurrentTime(); } catch (e) { return; }
      setPlaying(state === 1);
      const prev = last ?? t;
      last = t;
      if (Math.abs(t - prev) > 1.5) { done.clear(); return; } // user seeked
      if (state !== 1) return;
      const c = commentsRef.current.find((x) => !done.has(x.id) && x.text.trim() && x.t >= prev - 0.05 && x.t <= t + 0.05);
      if (!c) return;
      busy = true;
      done.add(c.id);
      try { p.pauseVideo(); } catch (e) { /* noop */ }
      setPlaying(true);
      setSpeakingId(c.id);
      try {
        await speak(await getVoiceClip(c.text, voiceRef.current));
      } catch (e) {
        await aiError(e, "Couldn't play this comment's voice.");
      }
      setSpeakingId(null);
      busy = false;
      try { last = p.getCurrentTime(); } catch (e) { /* noop */ }
      if (!stopped && enabledRef.current) { try { p.playVideo(); } catch (e) { /* noop */ } }
    }, 100);
    return () => { stopped = true; clearInterval(iv); try { audio?.pause(); } catch (e) { /* noop */ } setSpeakingId(null); };
  }, [enabled, playerRef, setSpeakingId]);

  return { playing };
}
